export const M10_UNIT_13 = {
  id: "m10-u13-if-whether",
  title: "Unit 13: Simple Questions, Reported",
  topic: "If / Whether + Verb + Preposition Collocations",
  iconType: "message-circle",
  grammarImage: "/m10-u13-if-whether.jpg",
  grammarText: "Unit focus: Reporting yes/no questions using if and whether — when both are possible, when only whether works, and how to report choices (whether...or) and decisions (whether to + infinitive). Combined with eight verb + preposition collocations that appear throughout the business and legal narrative.",

  sets: [

    // ===================================================================
    // SET 1: IF vs WHETHER — INTRODUCTION (B1+)
    // ===================================================================
    {
      id: "m10-u13-set1",
      title: "Set 1: Two Ways to Ask",
      context: `*Bucharest, Monday — Elena's first investor pitch*

Elena is 32, Romanian, and has spent four years building a renewable energy startup.
Today she is meeting her first serious investors.
She had prepared every answer — but she had also prepared her questions.
The right question, her mentor had told her, is worth more than the right answer.

*Bucarest, lunes — El primer discurso de inversión de Elena*

Elena tiene 32 años, es rumana y ha pasado cuatro años construyendo una startup de energía renovable.
Hoy se reúne con sus primeros inversores serios.
Había preparado todas las respuestas — pero también había preparado sus preguntas.
La pregunta correcta, le había dicho su mentor, vale más que la respuesta correcta.`,

      questions: [
        {
          type: "match",
          q: "Match if and whether to their uses:",
          pairs: [
            { left: "if", right: "más común en el habla y escritura informal — 'She asked if they were interested.'" },
            { left: "whether", right: "más formal — preferido en escritura y contextos profesionales" },
            { left: "both", right: "intercambiables en preguntas reportadas de sí/no" }
          ],
          a: "completed",
          successTip: "IF and WHETHER both introduce yes/no reported questions. IF = everyday speech. WHETHER = formal/written. 'She asked if they were ready' ✅ 'She asked whether they were ready' ✅ Both correct! 📋"
        },

        {
          type: "match",
          q: "Match the collocations to their meanings:",
          pairs: [
            { left: "invest in", right: "invertir en (dinero, tiempo, recursos)" },
            { left: "count on", right: "contar con / depender de (alguien o algo)" },
            { left: "provide for", right: "proveer para / mantener a (familia, necesidades)" }
          ],
          a: "completed",
          successTip: "INVEST IN (put resources into), COUNT ON (rely on), PROVIDE FOR (support financially/materially). All verb + preposition — never invest to, count from, or provide about! 💰🤝"
        },

        {
          type: "mc",
          q: "Mr. Okoro: 'Are you investing in solar or wind technology?' → Elena reported: He asked ___ she ___ in solar or wind technology.",
          options: ["if / was investing", "whether / was investing", "if / invested"],
          a: "if / was investing",
          tip: "Yes/No question → if or whether (both correct here). Is investing → was investing (backshift). Subject before verb!",
          successTip: "Both IF and WHETHER are correct for this yes/no question! IS INVESTING → WAS INVESTING (backshift). INVEST IN collocation intact inside the reported question! 💰✅"
        },

        {
          type: "mc",
          q: "Elena asked Mr. Okoro: 'Can we count on your full support?' → She asked ___ they ___ ___ his full support.",
          options: ["whether / could count on", "if / can count on", "whether / counted on"],
          a: "whether / could count on",
          tip: "Whether = slightly formal for investor context. Can → could (modal backshift). Count on = verb + preposition.",
          successTip: "WHETHER (formal investor context) + COULD COUNT ON (CAN → COULD backshift + collocation). The preposition ON stays attached to COUNT — never separated! 🤝"
        },

        {
          type: "mc",
          q: "Which two sentences are BOTH correct?",
          options: [
            "She asked if they were investing in the project. / She asked whether they were investing in the project.",
            "She asked if they were investing in the project. / She asked whether were they investing in the project.",
            "She asked if were they investing. / She asked whether they were investing."
          ],
          a: "She asked if they were investing in the project. / She asked whether they were investing in the project.",
          tip: "Both if and whether correct. Both with subject + verb (no inversion). Both with backshift.",
          successTip: "Both IF and WHETHER ✅ — same structure, same backshift, same word order. The ONLY difference is register (informal vs formal). Both sentences are perfectly correct! 📋"
        },

        {
          type: "mc",
          q: "Dani asked Elena: 'Are the investors providing for the infrastructure costs?' → He asked ___ the investors ___ for the infrastructure costs.",
          options: ["if / were providing", "whether / are providing", "if / provided"],
          a: "if / were providing",
          tip: "If = informal (colleague conversation). Are providing → were providing (backshift). Provide for = collocation.",
          successTip: "IF (informal colleague chat) + WERE PROVIDING (backshift) + PROVIDE FOR (collocation — the FOR is part of the verb!). Register + backshift + collocation! 🎯"
        },

        {
          type: "mc",
          q: "In her written report, Elena noted: 'Mr. Okoro asked ___ the company ___ in long-term infrastructure — and ___ the team ___ ___.'",
          options: ["whether / was investing / whether / could be counted on", "if / invested / if / could count on", "whether / had invested / if / was counting on"],
          a: "whether / was investing / whether / could be counted on",
          tip: "Written report = formal = whether (both). Was investing + could be counted on = backshifted. Two collocations!",
          successTip: "Written report = WHETHER (twice — formal). WAS INVESTING (backshift) + COULD BE COUNTED ON (passive backshift). INVEST IN + COUNT ON both appear! 💰🤝"
        },

        {
          type: "mc",
          q: "Mr. Okoro's assistant wrote: 'The entrepreneur asked ___ we ___ fully committed and ___ we ___ on her team's expertise.'",
          options: ["whether / were / whether / could count", "if / are / if / can count", "whether / had been / if / counted"],
          a: "whether / were / whether / could count",
          tip: "Written = whether (both). Were = backshift of are. Could count = backshift of can count. Count on = collocation.",
          successTip: "Whether × 2 (written formal) + WERE (backshift) + COULD COUNT ON (backshift + collocation). Two whether questions, consistent formality, correct backshift! 📋✅"
        },

        {
          type: "order",
          q: "Reorder: 'Elena preguntó si los inversores estaban dispuestos a invertir en la primera fase del proyecto.'",
          words: ["Elena", "asked", "whether", "the", "investors", "were", "willing", "to", "invest", "in", "the", "first", "phase", "of", "the", "project."],
          a: "Elena asked whether the investors were willing to invest in the first phase of the project.",
          extra: ["if", "invested", "had"]
        },

        {
          type: "input",
          q: "Meeting summary: 'Mr. Okoro asked ___ the company ___ ___ renewable energy exclusively — and ___ the team ___ be ___ ___.' (whether + invest in / whether + count on — past tense)",
          a: "whether / was investing / in / whether / could / counted / on",
          alts: ["Whether / was investing / in / whether / could be counted / on"],
          successTip: "WHETHER × 2 (formal) + WAS INVESTING IN (backshift + collocation) + COULD BE COUNTED ON (passive backshift + collocation). The full professional report! 💰🤝✅"
        }
      ]
    },

    // ===================================================================
    // SET 2: BACKSHIFT PRESENT → PAST (B1+)
    // ===================================================================
    {
      id: "m10-u13-set2",
      title: "Set 2: Moving the Tense Back",
      context: `*Bucharest, Tuesday — Elena's second meeting*

The investors had more questions than Elena expected.
Each one required a careful answer — and when she reported those questions
to Dani that evening, every tense had to move back.
"You can tell a lot about someone," Dani said,
"by what they're asking — and how far back the tenses go."

*Bucarest, martes — La segunda reunión de Elena*

Los inversores tenían más preguntas de las que Elena esperaba.
Cada una requería una respuesta cuidadosa — y cuando reportó esas preguntas
a Dani esa noche, cada tiempo verbal tenía que retroceder.
"Se puede saber mucho de alguien," dijo Dani,
"por lo que pregunta — y hasta qué punto retroceden los tiempos."`,

      questions: [
        {
          type: "match",
          q: "Match the backshift rules for if/whether clauses:",
          pairs: [
            { left: "Is/Are + verb-ing → Was/Were + verb-ing", right: "'Are you dreaming of expansion?' → asked if she was dreaming of expansion." },
            { left: "Has/Have + past participle → Had + past participle", right: "'Have you waited for this long?' → asked whether she had waited for this long." },
            { left: "Present Simple → Past Simple", right: "'Do you dream of success?' → asked if she dreamed of success." }
          ],
          a: "completed",
          successTip: "Same backshift rules inside if/whether clauses! Present Continuous → Past Continuous. Present Perfect → Past Perfect. Present Simple → Past Simple. The clause follows, the rule doesn't change! ⏪"
        },

        {
          type: "match",
          q: "Match the collocations to their meanings:",
          pairs: [
            { left: "dream of / dream about", right: "soñar con (ambición, deseo profundo)" },
            { left: "wait for", right: "esperar a / esperar que (persona, momento, resultado)" },
            { left: "pour into", right: "dedicar / volcar en (esfuerzo, dinero, energía)" }
          ],
          a: "completed",
          successTip: "DREAM OF/ABOUT (both correct — deep ambition), WAIT FOR (anticipate something), POUR INTO (invest intensely — time, money, energy). All need their prepositions! 💭⏳💧"
        },

        {
          type: "mc",
          q: "Investor: 'Are you dreaming of expanding to other markets?' → He asked ___ Elena ___ of expanding to other markets.",
          options: ["if / was dreaming", "whether / is dreaming", "if / dreamed"],
          a: "if / was dreaming",
          tip: "If = informal spoken question. Is dreaming → was dreaming (backshift). Dream of = collocation.",
          successTip: "IF (informal spoken) + WAS DREAMING (Present Continuous → Past Continuous backshift) + DREAM OF (collocation — OF stays!). Three elements correct! 💭"
        },

        {
          type: "mc",
          q: "Investor: 'Have you been waiting for this opportunity for long?' → She asked whether Elena ___ for the opportunity for long.",
          options: ["had been waiting", "has been waiting", "was waiting"],
          a: "had been waiting",
          tip: "Whether = formal. Has been waiting (Present Perfect Continuous) → had been waiting (Past Perfect Continuous).",
          successTip: "HAS BEEN WAITING → HAD BEEN WAITING (Present Perfect Continuous → Past Perfect Continuous). WHETHER (formal written context). WAIT FOR collocation! ⏳"
        },

        {
          type: "mc",
          q: "Investor: 'Do you dream about building something that lasts?' → He asked ___ she ___ about building something that ___.",
          options: ["if / dreamed / lasted", "whether / dreams / lasts", "if / had dreamed / last"],
          a: "if / dreamed / lasted",
          tip: "If = informal. Dream about = collocation (both of/about correct). Do dream → dreamed (backshift). Lasts → lasted (backshift in embedded clause).",
          successTip: "IF (informal) + DREAMED ABOUT (backshift + collocation) + LASTED (backshift in the embedded that-clause). Two backshifts in one reported question! ⏪💭"
        },

        {
          type: "mc",
          q: "Which reported question has the correct backshift?",
          options: [
            "She asked whether they had poured enough resources into the first phase.",
            "She asked whether they have poured enough resources into the first phase.",
            "She asked whether had they poured enough resources into the first phase."
          ],
          a: "She asked whether they had poured enough resources into the first phase.",
          tip: "Whether (formal). Had poured = backshift of have poured (Present Perfect → Past Perfect). Subject before verb — no inversion!",
          successTip: "Error detection! HAVE POURED ❌ (no backshift). HAD THEY POURED ❌ (inversion). HAD POURED ✅ (correct backshift) + WHETHER ✅ + no inversion ✅ + POUR INTO collocation ✅! 💧"
        },

        {
          type: "mc",
          q: "Dani asked Elena three questions after the meeting. He asked ___ the investors ___ in the project, ___ they ___ for a decision, and ___ anyone ___ serious money into it yet.",
          options: ["if / believed / whether / were waiting / if / had poured", "whether / believe / if / waited / whether / poured", "if / had believed / if / waited / whether / was pouring"],
          a: "if / believed / whether / were waiting / if / had poured",
          tip: "Three backshifts: believe→believed, are waiting→were waiting, has poured→had poured. Mix of if (informal) and whether (formal question).",
          successTip: "Three reported questions: BELIEVED (backshift) + WERE WAITING FOR (backshift + collocation) + HAD POURED INTO (backshift + collocation). Three collocations in three questions! ⏳💧🎯"
        },

        {
          type: "mc",
          q: "Elena reported to Dani: 'Mr. Okoro asked ___ I ___ about building this company since university — and ___ I ever ___ waiting for the right moment or ___ for it.'",
          options: ["if / had dreamed / whether / had stopped / fought", "whether / dreamed / if / stopped / was fighting", "if / was dreaming / whether / had been waiting / poured"],
          a: "if / had dreamed / whether / had stopped / fought",
          tip: "Had dreamed = Past Perfect backshift of dreamed. Had stopped = Past Perfect. Fought = Past Simple.",
          successTip: "IF + HAD DREAMED ABOUT (Past Perfect backshift + collocation) + WHETHER + HAD STOPPED WAITING FOR (backshift + collocation). Mr. Okoro was testing her commitment! 💭⏳"
        },

        {
          type: "order",
          q: "Reorder: 'Los inversores preguntaron si Elena había soñado con esta empresa durante mucho tiempo y si había esperado el momento adecuado para lanzarla.'",
          words: ["The", "investors", "asked", "if", "Elena", "had", "dreamed", "of", "the", "company", "for", "a", "long", "time", "and", "whether", "she", "had", "waited", "for", "the", "right", "moment", "to", "launch", "it."],
          a: "The investors asked if Elena had dreamed of the company for a long time and whether she had waited for the right moment to launch it.",
          extra: ["dreamed", "about", "was"]
        },

        {
          type: "input",
          q: "Dani's summary: 'The most personal question was ___ Elena ___ ___ this moment her whole career — and ___ she ___ everything she had ___ it.' (whether + wait for / whether + pour into — past perfect)",
          a: "whether / had been waiting / for / whether / had poured / into",
          alts: ["Whether / had been waiting / for / whether / had poured / into"],
          successTip: "WHETHER × 2 + HAD BEEN WAITING FOR (Past Perfect Continuous + collocation) + HAD POURED INTO (Past Perfect + collocation). Both WAIT FOR and POUR INTO perfectly backshifted! ⏳💧✅"
        }
      ]
    },

    // ===================================================================
    // SET 3: BACKSHIFT WILL/CAN → WOULD/COULD (B2-)
    // ===================================================================
    {
      id: "m10-u13-set3",
      title: "Set 3: Future and Ability, Moved Back",
      context: `*Bucharest, Wednesday — Dani's technical presentation*

Dani had prepared the technical side of the pitch.
His numbers were solid. His projections were clear.
But the investors wanted to know not just what was happening —
they wanted to know what would happen, what could happen,
and whether the team could be counted on to make it happen.

*Bucarest, miércoles — La presentación técnica de Dani*

Dani había preparado la parte técnica del discurso.
Sus números eran sólidos. Sus proyecciones eran claras.
Pero los inversores querían saber no solo lo que estaba ocurriendo —
querían saber qué ocurriría, qué podría ocurrir,
y si el equipo podía contar con su apoyo para hacerlo realidad.`,

      questions: [
        {
          type: "match",
          q: "Match the modal backshift rules:",
          pairs: [
            { left: "Will → Would", right: "'Will you pour funds into this?' → asked if they would pour funds into it." },
            { left: "Can → Could", right: "'Can we count on your expertise?' → asked whether they could count on his expertise." },
            { left: "Should → Should (no change)", right: "'Should we invest in this sector?' → asked whether they should invest in it." }
          ],
          a: "completed",
          successTip: "WILL → WOULD ✅ CAN → COULD ✅ SHOULD → SHOULD (no change — already past-form!) ✅ MAY → MIGHT ✅ These modal backshifts appear inside if/whether clauses just like in reported statements! 🔄"
        },

        {
          type: "mc",
          q: "Investor: 'Will you pour more funds into infrastructure next year?' → He asked ___ they ___ more funds into infrastructure the following year.",
          options: ["if / would pour", "whether / will pour", "if / poured"],
          a: "if / would pour",
          tip: "If = informal. Will → would (modal backshift). Pour into = collocation. Tomorrow → the following year (time shift).",
          successTip: "IF (informal) + WOULD POUR (WILL → WOULD backshift) + INTO (collocation preposition) + THE FOLLOWING YEAR (next year time shift). Four elements! 💧🔄"
        },

        {
          type: "mc",
          q: "Mr. Okoro: 'Can the infrastructure provide for a hundred thousand homes?' → He asked ___ the infrastructure ___ for a hundred thousand homes.",
          options: ["whether / could provide", "if / can provide", "whether / would provide"],
          a: "whether / could provide",
          tip: "Whether = formal investor question. Can → could (modal backshift). Provide for = collocation.",
          successTip: "WHETHER (formal) + COULD PROVIDE FOR (CAN → COULD backshift + collocation). The FOR is essential — PROVIDE alone changes the meaning! 🏠✅"
        },

        {
          type: "mc",
          q: "Dani: 'Will the project be able to provide for the local communities?' → She asked ___ the project ___ able to ___ for the local communities.",
          options: ["if / would be / provide", "whether / will be / provide", "if / could be / providing"],
          a: "if / would be / provide",
          tip: "If = informal (colleague). Will → would. Be able to provide for = full structure.",
          successTip: "IF (colleague — informal) + WOULD BE ABLE TO PROVIDE FOR (WILL → WOULD + full structure + collocation). The FOR stays at the end! 🤝💧"
        },

        {
          type: "mc",
          q: "Choose the correctly reported question: Original — 'Can we count on the government's backing?'",
          options: [
            "She asked whether they could count on the government's backing.",
            "She asked whether could they count on the government's backing.",
            "She asked whether they can count on the government's backing."
          ],
          a: "She asked whether they could count on the government's backing.",
          tip: "Whether (formal). Could = backshift of can. Subject (they) before verb (could count). Count on = collocation.",
          successTip: "Error detection! 'Could THEY count' ❌ (inversion). 'CAN count' ❌ (no backshift). 'They COULD COUNT ON' ✅ — subject before verb, modal backshifted, collocation intact! 🤝"
        },

        {
          type: "mc",
          q: "The investors asked Dani two questions. He reported: They asked ___ the system ___ for remote areas and ___ the team ___ ___ on external engineers.",
          options: ["whether / could provide / if / would count", "if / can provide / whether / will count", "whether / could provide / whether / would have to count"],
          a: "whether / could provide / if / would count",
          tip: "Written context (first) = whether. Informal (second) = if. Can→could, will→would. Provide for + count on = collocations.",
          successTip: "WHETHER (formal first question) + IF (informal second) — different registers in the same sequence! COULD PROVIDE FOR + WOULD COUNT ON — both modal backshifts + both collocations! 🏠🤝"
        },

        {
          type: "mc",
          q: "Elena asked Mr. Okoro at the end: 'Will you be able to invest in the second phase?' → She asked ___ he ___ able to ___ in the second phase.",
          options: ["whether / would be / invest", "if / will be / invest", "whether / could be / investing"],
          a: "whether / would be / invest",
          tip: "Whether = formal (end of meeting). Will → would. Invest in = collocation.",
          successTip: "WHETHER (formal close of meeting) + WOULD BE ABLE TO (WILL → WOULD) + INVEST IN (collocation). The final question of the pitch — reported precisely! 💰✅"
        },

        {
          type: "order",
          q: "Reorder: 'Los inversores preguntaron si el proyecto podría proveer para las comunidades locales y si el equipo podría contar con el apoyo gubernamental.'",
          words: ["The", "investors", "asked", "whether", "the", "project", "could", "provide", "for", "local", "communities", "and", "whether", "the", "team", "could", "count", "on", "government", "support."],
          a: "The investors asked whether the project could provide for local communities and whether the team could count on government support.",
          extra: ["if", "can", "will"]
        },

        {
          type: "mc",
          q: "Priya interviewed Dani for her article. She asked ___ the technology ___ really ___ for the energy needs of a whole region and ___ the company ___ ___ enough money into development.",
          options: ["whether / could provide / if / would pour", "if / can provide / whether / will pour", "whether / could provide / whether / had poured"],
          a: "whether / could provide / if / would pour",
          tip: "Article = whether (formal). Colleague follow-up = if (informal). Could provide for + would pour into = modal backshifts + collocations.",
          successTip: "WHETHER (article) + IF (follow-up) — register difference in one paragraph! COULD PROVIDE FOR + WOULD POUR INTO — both modals backshifted, both collocations correct! 🏠💧📰"
        },

        {
          type: "input",
          q: "End of day summary: 'Every investor asked ___ the project ___ ___ for thousands of families — and ___ the team ___ ___ on that promise.' (whether + provide for / whether + count on — would/could)",
          a: "whether / would provide / for / whether / could count / on",
          alts: ["Whether / could provide / for / whether / could count / on"],
          successTip: "WHETHER × 2 + WOULD/COULD PROVIDE FOR + COULD COUNT ON — both modal backshifts, both collocations, both with correct prepositions. The core promise of the pitch! 🏠🤝✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: NEGATIVE BACKSHIFT (B2-)
    // ===================================================================
    {
      id: "m10-u13-set4",
      title: "Set 4: Questions That Challenge",
      context: `*Bucharest, Thursday — a sceptical investor arrives*

Not all the investors were friendly.
One had done his own research — and his questions were not looking for confirmation.
They were looking for cracks.
Elena had been warned this would happen.
She had also been warned that the hardest questions
are always the ones that begin with "haven't you" and "didn't you."

*Bucarest, jueves — Llega un inversor escéptico*

No todos los inversores eran amistosos.
Uno había hecho su propia investigación — y sus preguntas no buscaban confirmación.
Buscaban grietas.
Elena había sido advertida de que esto ocurriría.
También le habían advertido que las preguntas más difíciles
son siempre las que empiezan con "¿acaso no has?" y "¿no hiciste?"`,

      questions: [
        {
          type: "match",
          q: "Match the negative backshift rules:",
          pairs: [
            { left: "Haven't/Hasn't → Hadn't", right: "'Haven't you invested in this before?' → asked whether she hadn't invested before." },
            { left: "Didn't → Hadn't", right: "'Didn't you wait for official approval?' → asked if she hadn't waited for official approval." },
            { left: "Won't → Wouldn't", right: "'Won't this pour pressure into the team?' → asked if this wouldn't pour pressure into the team." }
          ],
          a: "completed",
          successTip: "Negative backshift follows the same pattern! Haven't → Hadn't (Present Perfect neg → Past Perfect neg). Didn't → Hadn't (Past Simple neg → Past Perfect neg). Won't → Wouldn't! ⏪"
        },

        {
          type: "match",
          q: "Match the collocations to their meanings:",
          pairs: [
            { left: "accused of", right: "acusado de (cargo formal o informal)" },
            { left: "fight against", right: "luchar contra (oposición, injusticia, obstáculo)" },
            { left: "wait for", right: "esperar a / esperar que (persona, resultado, momento)" }
          ],
          a: "completed",
          successTip: "ACCUSED OF (charged with something negative), FIGHT AGAINST (oppose/resist), WAIT FOR (anticipate). These prepositions are fixed — accused FROM or fight with change the meaning entirely! ⚖️💪⏳"
        },

        {
          type: "mc",
          q: "Investor: 'Haven't you been accused of cutting corners before?' → He asked ___ she ___ of cutting corners before.",
          options: ["whether / hadn't been accused", "if / hasn't been accused", "whether / wasn't accused"],
          a: "whether / hadn't been accused",
          tip: "Whether = formal (aggressive question in formal context). Haven't been accused → hadn't been accused (backshift). Accused of = collocation.",
          successTip: "WHETHER (formal confrontational context) + HADN'T BEEN ACCUSED (Present Perfect neg → Past Perfect neg backshift) + ACCUSED OF (collocation intact). Three elements! ⚖️"
        },

        {
          type: "mc",
          q: "Investor: 'Didn't you wait for the environmental report before investing?' → He asked ___ she ___ for the environmental report before investing.",
          options: ["if / hadn't waited", "whether / didn't wait", "if / wasn't waiting"],
          a: "if / hadn't waited",
          tip: "If = informal challenge. Didn't wait → hadn't waited (Past Simple neg → Past Perfect neg). Wait for = collocation.",
          successTip: "IF (blunt informal challenge) + HADN'T WAITED (Past Simple neg → Past Perfect neg backshift) + WAIT FOR (collocation). The investor was implying she acted too hastily! ⏳"
        },

        {
          type: "mc",
          q: "Investor: 'Won't this project pour more costs into the public budget?' → He asked ___ the project ___ more costs into the public budget.",
          options: ["whether / wouldn't pour", "if / won't pour", "whether / didn't pour"],
          a: "whether / wouldn't pour",
          tip: "Whether = formal. Won't → wouldn't (modal backshift negative). Pour into = collocation.",
          successTip: "WHETHER (formal) + WOULDN'T POUR (WON'T → WOULDN'T backshift) + POUR INTO (collocation). The investor was questioning future risk! 💧⚖️"
        },

        {
          type: "mc",
          q: "Choose the correctly reported negative question: Original — 'Haven't you been fighting against this regulation for years?'",
          options: [
            "He asked whether she hadn't been fighting against this regulation for years.",
            "He asked whether she hasn't been fighting against this regulation for years.",
            "He asked whether hadn't she been fighting against this regulation for years."
          ],
          a: "He asked whether she hadn't been fighting against this regulation for years.",
          tip: "Whether (formal). Hadn't been fighting = Present Perfect Continuous neg → Past Perfect Continuous neg. No inversion. Fight against = collocation.",
          successTip: "Error detection! HASN'T ❌ (no backshift). HADN'T SHE ❌ (inversion). HADN'T BEEN FIGHTING ✅ (backshift) + WHETHER ✅ + FIGHT AGAINST ✅ (collocation). All three correct! 💪"
        },

        {
          type: "mc",
          q: "Elena reported to Dani: 'He asked ___ I ___ of mismanagement and ___ I ___ ___ for a formal investigation.'",
          options: ["whether / had been accused / if / hadn't been waiting", "if / was accused / whether / was waiting", "whether / accused / if / waited"],
          a: "whether / had been accused / if / hadn't been waiting",
          tip: "Whether (formal accusation context). Had been accused of = Past Perfect passive + collocation. If = informal follow-up. Hadn't been waiting for = Past Perfect Continuous neg + collocation.",
          successTip: "WHETHER + HAD BEEN ACCUSED OF (formal + Past Perfect passive + collocation) + IF + HADN'T BEEN WAITING FOR (informal + Past Perfect Continuous neg + collocation). Both challenges reported! ⚖️⏳"
        },

        {
          type: "mc",
          q: "The investor's most aggressive question: 'Don't you know you'll be accused of fraud if this fails?' → He demanded to know ___ she ___ she ___ of fraud if the project ___.",
          options: ["whether / didn't know / would be accused / failed", "if / knew / was accused / would fail", "whether / hadn't known / would be accused / failed"],
          a: "whether / didn't know / would be accused / failed",
          tip: "Whether = formal confrontational. Didn't know = Present Simple neg backshift. Would be accused of = will → would passive + collocation. Failed = if-clause (no backshift needed in condition).",
          successTip: "WHETHER + DIDN'T KNOW (backshift) + WOULD BE ACCUSED OF (WILL → WOULD + passive + collocation) + FAILED (conditional clause). The most complex challenge of the meeting! ⚖️"
        },

        {
          type: "order",
          q: "Reorder: 'El inversor preguntó si Elena no había esperado la aprobación y si no habría sido acusada de actuar ilegalmente.'",
          words: ["The", "investor", "asked", "whether", "Elena", "hadn't", "waited", "for", "approval", "and", "whether", "she", "wouldn't", "have", "been", "accused", "of", "acting", "illegally."],
          a: "The investor asked whether Elena hadn't waited for approval and whether she wouldn't have been accused of acting illegally.",
          extra: ["if", "wasn't", "didn't"]
        },

        {
          type: "input",
          q: "Elena's response notes: 'Every question he asked was about ___ I ___ of something — or ___ I ___ hard enough ___ it.' (whether + be accused of / whether + fight against — past perfect)",
          a: "whether / had been accused / whether / had fought / against",
          alts: ["Whether / had been accused of / whether / had fought against"],
          successTip: "WHETHER × 2 + HAD BEEN ACCUSED OF (passive Past Perfect + collocation) + HAD FOUGHT AGAINST (Past Perfect + collocation). Both negative collocations — the investor's whole strategy exposed! ⚖️💪✅"
        }
      ]
    },

    // ===================================================================
    // SET 5: WHETHER...OR (B2-)
    // ===================================================================
    {
      id: "m10-u13-set5",
      title: "Set 5: This or That?",
      context: `*Bucharest, Friday — Elena's board meeting*

The board had been following the investor meetings closely.
Now they had their own questions — questions that weren't simple yes or no.
They were questions about choices.
Solar or wind? Domestic or international? Invest now or wait?
"The hardest questions," Elena's mentor had once said,
"are the ones where both answers are possible."

*Bucarest, viernes — La reunión del consejo de Elena*

El consejo había seguido de cerca las reuniones con inversores.
Ahora tenían sus propias preguntas — preguntas que no eran simples de sí o no.
Eran preguntas sobre elecciones.
¿Solar o eólica? ¿Nacional o internacional? ¿Invertir ahora o esperar?
"Las preguntas más difíciles," dijo una vez el mentor de Elena,
"son aquellas en las que ambas respuestas son posibles."`,

      questions: [
        {
          type: "match",
          q: "Match the whether...or rules:",
          pairs: [
            { left: "whether...or", right: "reporta una elección entre dos opciones — NEVER if...or" },
            { left: "structure", right: "asked whether X or Y — subject + verb, no inversion" },
            { left: "example", right: "'Are you investing in solar or wind?' → asked whether they were investing in solar or wind." }
          ],
          a: "completed",
          successTip: "WHETHER...OR reports a choice! 'Asked whether X or Y' ✅ NEVER 'asked if X or Y' ❌ Whether is REQUIRED when there are alternatives. IF cannot be used with OR! 🔄"
        },

        {
          type: "mc",
          q: "Board member: 'Are you investing in solar or wind technology?' → She asked ___ Elena ___ in solar ___ wind technology.",
          options: ["whether / was investing / or", "if / was investing / or", "whether / invested / or"],
          a: "whether / was investing / or",
          tip: "Two alternatives = whether...or (NOT if...or). Was investing = backshift. Whether is required with OR!",
          successTip: "WHETHER...OR (NEVER if...or with alternatives!) + WAS INVESTING (backshift) + INVEST IN (collocation). Whether is mandatory when reporting a choice between two options! 🔄💰"
        },

        {
          type: "mc",
          q: "Board member: 'Are you dreaming of domestic or international expansion?' → She asked ___ Elena ___ of domestic ___ international expansion.",
          options: ["whether / was dreaming / or", "if / dreamed / or", "whether / had dreamed / or"],
          a: "whether / was dreaming / or",
          tip: "Two alternatives = whether...or. Is dreaming → was dreaming (backshift). Dream of = collocation.",
          successTip: "WHETHER...OR (alternatives = whether required) + WAS DREAMING (backshift) + DREAM OF (collocation — OF stays!). 💭🔄"
        },

        {
          type: "mc",
          q: "Choose the correct reported question: Original — 'Will you pour resources into the domestic market or the international one?'",
          options: [
            "She asked whether they would pour resources into the domestic market or the international one.",
            "She asked if they would pour resources into the domestic market or the international one.",
            "She asked whether would they pour resources into the domestic or international market."
          ],
          a: "She asked whether they would pour resources into the domestic market or the international one.",
          tip: "Whether...or = required for alternatives. Would = backshift of will. Subject before verb — no inversion. Pour into = collocation.",
          successTip: "Error detection! IF...OR ❌ (must be whether with alternatives). WOULD THEY ❌ (inversion). WHETHER + THEY + WOULD POUR INTO + OR ✅ — all correct! 💧🔄"
        },

        {
          type: "mc",
          q: "The board asked Elena ___ she ___ on government support ___ private investors to fund the next phase.",
          options: ["whether / was counting / or", "if / counted / or", "whether / would count / or"],
          a: "whether / was counting / or",
          tip: "Whether...or = alternatives. Is counting → was counting (backshift). Count on = collocation.",
          successTip: "WHETHER...OR + WAS COUNTING ON (backshift + collocation). Two alternatives (government vs private) = WHETHER required! COUNT ON with the preposition intact! 🤝🔄"
        },

        {
          type: "mc",
          q: "The hardest question: 'Will you invest in the project now or wait for better conditions?' → They asked ___ she ___ in the project now ___ ___ for better conditions.",
          options: ["whether / would invest / or / wait", "whether / was investing / or / waited", "if / would invest / or / wait"],
          a: "whether / would invest / or / wait",
          tip: "Whether...or with two verb alternatives. Would invest OR wait = parallel infinitives (no to). Will → would (backshift). Invest in + wait for = both collocations!",
          successTip: "WHETHER + WOULD INVEST IN OR WAIT FOR — two collocations in one whether...or question! WOULD INVEST (OR) WAIT = parallel structure, no repeated TO. The perfect alternative question! 💰⏳🔄"
        },

        {
          type: "mc",
          q: "Dani reported the board's questions: 'They kept asking ___ we ___ in infrastructure ___ technology and ___ we ___ on the original team ___ bring in new people.'",
          options: ["whether / would invest / or / whether / would count / or", "if / invested / or / if / counted / or", "whether / were investing / or / whether / would count / or"],
          a: "whether / would invest / or / whether / would count / or",
          tip: "Two whether...or questions. Would invest or (spend on) = alternatives. Would count on or bring in = alternatives. Both modals backshifted.",
          successTip: "Two WHETHER...OR questions — both with WOULD (backshift). INVEST IN...OR + COUNT ON...OR. The board was testing every major decision! 💰🤝🔄"
        },

        {
          type: "order",
          q: "Reorder: 'El consejo preguntó si Elena soñaba con expandirse a nivel nacional o internacional — y si invertiría en ambos o esperaría los resultados primero.'",
          words: ["The", "board", "asked", "whether", "Elena", "was", "dreaming", "of", "domestic", "or", "international", "expansion", "and", "whether", "she", "would", "invest", "in", "both", "or", "wait", "for", "results", "first."],
          a: "The board asked whether Elena was dreaming of domestic or international expansion and whether she would invest in both or wait for results first.",
          extra: ["if", "dreamed", "had"]
        },

        {
          type: "mc",
          q: "Elena told Dani: 'The board wanted to know ___ I ___ of a small sustainable company ___ a global corporation — and ___ I ___ resources into quality ___ quantity.'",
          options: ["whether / was dreaming / or / whether / would pour / or", "if / dreamed / or / if / poured / or", "whether / had dreamed / or / whether / was pouring / or"],
          a: "whether / was dreaming / or / whether / would pour / or",
          tip: "Whether...or (twice). Was dreaming of = backshift + collocation. Would pour into = modal backshift + collocation.",
          successTip: "WHETHER...OR × 2 + WAS DREAMING OF (backshift + collocation) + WOULD POUR INTO (modal backshift + collocation). The board's two biggest philosophical questions! 💭💧🔄"
        },

        {
          type: "input",
          q: "Board summary: 'The final vote was about ___ to ___ in the domestic market ___ to ___ for international conditions to improve.' (whether + invest in / wait for)",
          a: "whether / invest / or / wait",
          alts: ["Whether / invest in / or / wait for"],
          successTip: "WHETHER TO INVEST IN OR WAIT FOR — this is BOTH whether...or AND whether to + infinitive (next set!). Two alternatives + two collocations. The perfect bridge to Set 6! 💰⏳🔄✅"
        }
      ]
    },

    // ===================================================================
    // SET 6: WHETHER TO + INFINITIVE (B2)
    // ===================================================================
    {
      id: "m10-u13-set6",
      title: "Set 6: The Decision Question",
      context: `*Bucharest, the weekend — Dani's internal dilemma*

The board meeting had left Dani with a question he couldn't answer.
Not a question from the investors — a question to himself.
Should he stay with Elena's project or take a better-paid offer?
Should he count on her vision or provide for his own future first?
These were not questions anyone else could answer.
They were questions of whether to.

*Bucarest, el fin de semana — El dilema interno de Dani*

La reunión del consejo había dejado a Dani con una pregunta que no podía responder.
No una pregunta de los inversores — una pregunta para sí mismo.
¿Debería quedarse con el proyecto de Elena o aceptar una oferta mejor pagada?
¿Debería confiar en su visión o proveer para su propio futuro primero?
Estas no eran preguntas que nadie más pudiera responder.
Eran preguntas de si hacerlo o no.`,

      questions: [
        {
          type: "match",
          q: "Match the whether to + infinitive rules:",
          pairs: [
            { left: "whether to + infinitive", right: "reporta una decisión o dilema — NEVER if to" },
            { left: "structure", right: "wondered/decided/couldn't decide WHETHER TO + base verb" },
            { left: "example", right: "'Should I invest in this?' → She wondered whether to invest in it." }
          ],
          a: "completed",
          successTip: "WHETHER TO + infinitive reports a decision or dilemma. NEVER 'if to' ❌ — only WHETHER TO ✅. 'She wondered whether to count on him' = she was deciding. IF TO is not possible in English! 🤔"
        },

        {
          type: "mc",
          q: "Dani asked himself: 'Should I count on Elena's promise?' → He wondered ___ ___ on Elena's promise.",
          options: ["whether to count", "if to count", "whether counting"],
          a: "whether to count",
          tip: "Internal decision = wondered whether to. NEVER if to. Count on = collocation.",
          successTip: "WHETHER TO COUNT ON (NEVER if to!) — the whether to + infinitive structure for internal decisions. COUNT ON collocation with TO embedded! 🤔🤝"
        },

        {
          type: "mc",
          q: "Elena herself couldn't decide: 'Should I pour more of my savings into the company?' → She couldn't decide ___ ___ more of her savings into the company.",
          options: ["whether to pour", "if to pour", "whether pouring"],
          a: "whether to pour",
          tip: "Couldn't decide = decision dilemma. Whether to pour = correct. If to is not possible. Pour into = collocation.",
          successTip: "COULDN'T DECIDE WHETHER TO POUR INTO — the decision structure perfectly formed! WHETHER TO (not if to!) + POUR INTO (collocation). 💧🤔"
        },

        {
          type: "mc",
          q: "Mr. Okoro told his colleague: 'I'm not sure — should I invest in this company?' → He wasn't sure ___ ___ in Elena's company.",
          options: ["whether to invest", "if to invest", "whether investing"],
          a: "whether to invest",
          tip: "Wasn't sure = uncertainty/decision. Whether to invest = correct. If to = not possible. Invest in = collocation.",
          successTip: "WASN'T SURE WHETHER TO INVEST IN — classic whether to + infinitive after 'sure'. WHETHER TO (not if to!) + INVEST IN (collocation intact). 💰🤔"
        },

        {
          type: "mc",
          q: "Choose the INCORRECT sentence:",
          options: [
            "She wondered if to count on the government's support.",
            "She wondered whether to count on the government's support.",
            "She couldn't decide whether to count on the government's support."
          ],
          a: "She wondered if to count on the government's support.",
          tip: "IF TO is not possible in English! Only WHETHER TO is correct before an infinitive.",
          successTip: "IF TO ❌ — this is simply not a structure in English! Always WHETHER TO when reporting a decision or dilemma. 'Wondered if to' = error. 'Wondered whether to' = correct! 🚫"
        },

        {
          type: "mc",
          q: "Dani's diary: 'The question wasn't ___ the company ___ for his family in ten years — it was ___ stay and help build it.'",
          options: ["whether / would provide / whether to", "if / provides / if to", "whether / could provide / whether to"],
          a: "whether / would provide / whether to",
          tip: "Whether (yes/no reported question). Would provide for = backshifted modal + collocation. Whether to stay = decision infinitive.",
          successTip: "Two different WHETHER structures in one sentence! WHETHER + WOULD PROVIDE FOR (reported yes/no question + modal backshift + collocation) + WHETHER TO STAY (decision infinitive). Two structures, both correct! 🏠🤔"
        },

        {
          type: "mc",
          q: "Elena and Dani both faced the same choice. Elena ___ ___ in the domestic market first. Dani ___ ___ for a better offer.",
          options: ["wondered whether to invest / debated whether to wait", "asked if to invest / wondered if to wait", "decided whether investing / considered whether waiting"],
          a: "wondered whether to invest / debated whether to wait",
          tip: "Whether to + infinitive after wondered and debated. NEVER if to. Invest in + wait for = collocations.",
          successTip: "WONDERED WHETHER TO INVEST IN + DEBATED WHETHER TO WAIT FOR — both whether to + infinitive structures. Two characters, two decisions, two collocations! 💰⏳🤔"
        },

        {
          type: "order",
          q: "Reorder: 'Dani no podía decidir si quedarse y contar con la promesa de Elena o esperar una oferta mejor.'",
          words: ["Dani", "couldn't", "decide", "whether", "to", "stay", "and", "count", "on", "Elena's", "promise", "or", "wait", "for", "a", "better", "offer."],
          a: "Dani couldn't decide whether to stay and count on Elena's promise or wait for a better offer.",
          extra: ["if", "to stay", "whether"]
        },

        {
          type: "mc",
          q: "The hardest question of the week was ___ to ___ everything into a project that might fail, ___ to ___ for better conditions, and ultimately ___ ___ ___ his family's future on one decision.",
          options: ["whether / pour / whether / wait / whether to count on", "if / pour / if / wait / if to count on", "whether / invest / whether / dream / whether to provide for"],
          a: "whether / pour / whether / wait / whether to count on",
          tip: "Three whether to + infinitive structures. Pour into + wait for + count on = three collocations. All whether to (never if to).",
          successTip: "WHETHER TO POUR INTO + WHETHER TO WAIT FOR + WHETHER TO COUNT ON — three collocations, three decisions, three WHETHER TO structures! IF TO never appears — only WHETHER TO! 💧⏳🤝🤔"
        },

        {
          type: "input",
          q: "Dani's final reflection: 'In the end, the question was simple: ___ ___ in something he believed in, or ___ ___ for something safer to come along.' (whether to invest / wait for)",
          a: "whether / to invest / to wait / for",
          alts: ["Whether to invest in / or wait for"],
          successTip: "WHETHER TO INVEST IN OR WAIT FOR — the perfect whether...or + whether to combination. Both collocations, the decision structure, one clean sentence. Dani made his choice! 💰⏳🤔✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: REGISTER — IF (INFORMAL) vs WHETHER (FORMAL) (B2)
    // ===================================================================
    {
      id: "m10-u13-set7",
      title: "Set 7: How Formal Is the Question?",
      context: `*Bucharest, the following Monday — Priya interviews Elena*

Priya had come to write a profile piece on Elena.
She noticed something: the same question sounded different
depending on who was asking it and where it would appear.
In conversation with Dani, Elena used one word.
In her legal documents, she used another.
Same question. Different register. Different word.

*Bucarest, el lunes siguiente — Priya entrevista a Elena*

Priya había venido a escribir un artículo de perfil sobre Elena.
Notó algo: la misma pregunta sonaba diferente
dependiendo de quién la hacía y dónde aparecería.
En conversación con Dani, Elena usaba una palabra.
En sus documentos legales, usaba otra.
La misma pregunta. Diferente registro. Diferente palabra.`,

      questions: [
        {
          type: "match",
          q: "Match the register rules for if and whether:",
          pairs: [
            { left: "if — informal", right: "Spoken conversation, casual writing, everyday reporting" },
            { left: "whether — formal", right: "Legal documents, journalism, academic writing, after prepositions" },
            { left: "the question of WHETHER", right: "After prepositions — always whether, never if" }
          ],
          a: "completed",
          successTip: "IF = everyday speech and writing. WHETHER = formal, written, legal. AND: after prepositions ('the question OF WHETHER', 'a debate ABOUT WHETHER') — always WHETHER, never IF! 📋⚖️"
        },

        {
          type: "mc",
          q: "Elena to Dani (casual conversation): 'Do you know if the investors have poured enough money into phase two yet?'",
          options: [
            "Correct — if is appropriate in casual conversation between colleagues.",
            "Incorrect — whether is always required.",
            "Correct — but only because it's a question, not a statement."
          ],
          a: "Correct — if is appropriate in casual conversation between colleagues.",
          tip: "Casual conversation between colleagues = if is perfectly correct.",
          successTip: "IF ✅ in casual conversation! Between colleagues, in speech, in everyday writing — IF is natural and correct. POUR INTO collocation present. Register determines the word! 💧"
        },

        {
          type: "mc",
          q: "Elena's legal document: 'The question of ___ the company had been waiting for official approval was central to the case.'",
          options: ["whether", "if", "both are correct here"],
          a: "whether",
          tip: "After a preposition (of), only WHETHER is possible. Never 'the question of if'.",
          successTip: "After OF → WHETHER only! 'The question OF WHETHER' ✅ 'The question OF IF' ❌ This is the rule after prepositions — always WHETHER! WAIT FOR collocation present! ⚖️"
        },

        {
          type: "mc",
          q: "Priya's article (formal journalism): 'There was considerable debate about ___ the company had been accused of misusing funds.'",
          options: ["whether", "if", "both are equally correct"],
          a: "whether",
          tip: "After a preposition (about) = whether only. Formal journalism also prefers whether.",
          successTip: "ABOUT WHETHER ✅ — after preposition = WHETHER only! Plus formal journalism prefers WHETHER anyway. ACCUSED OF collocation: the OF belongs to 'accused', not to 'whether'! ⚖️📰"
        },

        {
          type: "mc",
          q: "Elena to Dani (text message): 'Did they say ___ they'd invest in the second round?' Official press release: 'The board announced ___ additional investment ___ be forthcoming.'",
          options: ["if / whether / would", "whether / if / will", "if / whether / will"],
          a: "if / whether / would",
          tip: "Text message = informal = if. Press release = formal = whether. Will → would (backshift in reported speech). Invest in = collocation.",
          successTip: "IF (text message — informal!) + WHETHER (press release — formal!) + WOULD INVEST IN (backshift + collocation). Same question, same answer — different register, different word! 💰📱📋"
        },

        {
          type: "mc",
          q: "The legal dispute raised the question of ___ Elena ___ fighting against unfair regulations or ___ she ___ of personal profit.",
          options: ["whether / was / whether / had been dreaming", "if / fought / if / dreamed", "whether / had been fighting / whether / was dreaming"],
          a: "whether / was / whether / had been dreaming",
          tip: "The question of whether = after preposition = whether. Two collocations: fight against + dream of.",
          successTip: "THE QUESTION OF WHETHER × 2 (after preposition = whether always!) + WAS FIGHTING AGAINST (backshift + collocation) + HAD BEEN DREAMING OF (backshift + collocation). Legal precision! ⚖️💪💭"
        },

        {
          type: "mc",
          q: "Priya asked Elena directly (in the interview): '___ you worried about the investigation?' Elena's answer in the official transcript: 'She was asked ___ she ___ concerned about the investigation.'",
          options: ["Were / whether / was", "Are / if / was", "Were / if / was"],
          a: "Were / whether / was",
          tip: "Direct question = Were you. Official transcript = formal = whether. Was = backshift.",
          successTip: "Direct interview: 'WERE YOU worried?' → Official transcript: 'WHETHER SHE WAS' (formal, backshifted). The same question — two registers — IF in chat, WHETHER in print! 📰"
        },

        {
          type: "order",
          q: "Reorder (formal legal version): 'El documento preguntaba si la empresa había esperado la aprobación necesaria y si había sido acusada de actuar sin licencia.'",
          words: ["The", "document", "questioned", "whether", "the", "company", "had", "waited", "for", "the", "necessary", "approval", "and", "whether", "it", "had", "been", "accused", "of", "acting", "without", "a", "licence."],
          a: "The document questioned whether the company had waited for the necessary approval and whether it had been accused of acting without a licence.",
          extra: ["if", "waited", "accused"]
        },

        {
          type: "mc",
          q: "Priya's article used WHETHER throughout. Her editor said one sentence needed to change: 'Elena told me if she had always dreamed of building something lasting.' The correction:",
          options: [
            "Elena told me whether she had always dreamed of building something lasting.",
            "Elena told me if she dreamed of building something lasting.",
            "Elena said if she would dream of building something lasting."
          ],
          a: "Elena told me whether she had always dreamed of building something lasting.",
          tip: "In formal writing/journalism, whether is preferred over if. Had always dreamed of = Past Perfect + collocation.",
          successTip: "TOLD ME WHETHER (formal journalism — whether preferred!) + HAD ALWAYS DREAMED OF (Past Perfect + collocation). The editor was right — WHETHER fits the professional register! 📰💭"
        },

        {
          type: "input",
          q: "Legal summary: 'The central question was ___ the company ___ ___ government contracts while ___ ___ the charges against it.' (whether + wait for / fight against — past perfect continuous)",
          a: "whether / had been waiting / for / fighting / against",
          alts: ["Whether / had been waiting for / fighting against"],
          successTip: "WHETHER (after central question = formal!) + HAD BEEN WAITING FOR (Past Perfect Continuous + collocation) + FIGHTING AGAINST (collocation — while + gerund). Legal precision + two collocations! ⚖️⏳💪✅"
        }
      ]
    },

    // ===================================================================
    // SET 8: QUESTION SEQUENCES (B2)
    // ===================================================================
    {
      id: "m10-u13-set8",
      title: "Set 8: Questions in Court",
      context: `*Bucharest, one year later — the investigation*

Elena had been accused of misusing government funds.
She said she had been fighting against the accusation for months.
The investigation had questions — many of them.
Her lawyer reported each one carefully.
"In a courtroom," her lawyer told her,
"the way a question is reported can change everything."

*Bucarest, un año después — la investigación*

Elena había sido acusada de malversación de fondos públicos.
Dijo que había estado luchando contra la acusación durante meses.
La investigación tenía preguntas — muchas.
Su abogado reportaba cada una con cuidado.
"En una sala del tribunal," le dijo su abogado,
"la forma en que se reporta una pregunta puede cambiarlo todo."`,

      questions: [
        {
          type: "mc",
          q: "Investigator Q1: 'Did you invest in the project using public funds?' → Her lawyer reported: The investigator asked ___ she ___ in the project using public funds.",
          options: ["whether / had invested", "if / invested", "whether / was investing"],
          a: "whether / had invested",
          tip: "Legal context = whether (formal). Did invest → had invested (Past Simple → Past Perfect). Invest in = collocation.",
          successTip: "WHETHER (legal — always formal!) + HAD INVESTED (Past Simple → Past Perfect backshift) + INVEST IN (collocation). First question of the investigation reported! ⚖️💰"
        },

        {
          type: "mc",
          q: "Investigator Q2: 'Have you been accused of this kind of thing before?' → Her lawyer noted: The investigator asked ___ she ___ of this kind of thing before.",
          options: ["whether / had ever been accused", "if / has been accused", "whether / was accused"],
          a: "whether / had ever been accused",
          tip: "Whether (formal legal). Has been accused → had been accused (Present Perfect → Past Perfect). Accused of = collocation.",
          successTip: "WHETHER (formal) + HAD EVER BEEN ACCUSED OF (Present Perfect → Past Perfect + passive + collocation). ACCUSED OF — the OF is fixed! ⚖️"
        },

        {
          type: "mc",
          q: "Investigator Q3: 'Were you waiting for official clearance before proceeding?' → The lawyer wrote: The investigator asked ___ she ___ for official clearance before proceeding.",
          options: ["whether / had been waiting", "if / was waiting", "whether / waited"],
          a: "whether / had been waiting",
          tip: "Whether (formal). Was waiting → had been waiting (Past Continuous → Past Perfect Continuous). Wait for = collocation.",
          successTip: "WHETHER (formal) + HAD BEEN WAITING FOR (Past Continuous → Past Perfect Continuous backshift + collocation). The continuous tense moves back one full step! ⏳⚖️"
        },

        {
          type: "mc",
          q: "Investigator Q4: 'Haven't you been fighting against regulation in this sector for years?' → The lawyer noted: The investigator asked ___ she ___ against regulation for years.",
          options: ["whether / hadn't been fighting", "if / wasn't fighting", "whether / hadn't fought"],
          a: "whether / hadn't been fighting",
          tip: "Whether (formal legal). Haven't been fighting → hadn't been fighting (Present Perfect Continuous neg → Past Perfect Continuous neg). Fight against = collocation.",
          successTip: "WHETHER (formal) + HADN'T BEEN FIGHTING AGAINST (Present Perfect Continuous neg → Past Perfect Continuous neg + collocation). Complex but correct! 💪⚖️"
        },

        {
          type: "mc",
          q: "Investigator Q5: 'Will you continue to pour funds into this venture?' → The lawyer wrote: The investigator asked ___ she ___ to ___ funds into the venture.",
          options: ["whether / would continue / pour", "if / will continue / pour", "whether / would continue / pouring"],
          a: "whether / would continue / pour",
          tip: "Whether (formal). Will → would (modal backshift). Continue to pour into = full structure. Pour into = collocation.",
          successTip: "WHETHER + WOULD CONTINUE TO POUR INTO (WILL → WOULD + collocation). Five questions reported — all WHETHER (legal context), all correctly backshifted! 💧⚖️"
        },

        {
          type: "mc",
          q: "Elena's lawyer summarised all five questions: The investigation focused on ___ she ___ public funds, ___ she ___ of similar conduct, ___ she ___ for approval, ___ she ___ against oversight, and ___ she ___ further resources into the project.",
          options: [
            "whether / had invested / whether / had been accused / whether / had waited / whether / had been fighting / whether / would pour",
            "if / invested / if / accused / if / waited / if / fought / if / would pour",
            "whether / was investing / whether / was accused / whether / waited / whether / fought / whether / poured"
          ],
          a: "whether / had invested / whether / had been accused / whether / had waited / whether / had been fighting / whether / would pour",
          tip: "All legal = all whether. All Past Perfect or Past Perfect Continuous backshifts (except would pour = modal). Five collocations: invest in, accused of, wait for, fight against, pour into.",
          successTip: "FIVE WHETHER questions, FIVE collocations, FIVE correct backshifts! Invest in + accused of + wait for + fight against + pour into — the whole legal case in one sentence! ⚖️🏆"
        },

        {
          type: "order",
          q: "Reorder: 'El abogado informó de que la investigación había preguntado si Elena había esperado la aprobación y si había estado luchando contra la acusación durante meses.'",
          words: ["The", "lawyer", "reported", "that", "the", "investigation", "had", "asked", "whether", "Elena", "had", "waited", "for", "approval", "and", "whether", "she", "had", "been", "fighting", "against", "the", "charges", "for", "months."],
          a: "The lawyer reported that the investigation had asked whether Elena had waited for approval and whether she had been fighting against the charges for months.",
          extra: ["if", "was", "whether"]
        },

        {
          type: "mc",
          q: "The investigation also wanted to know ___ Elena ___ of building a sustainable company ___ personal gain — and ___ she ___ ___ on public trust to avoid scrutiny.",
          options: ["whether / had been dreaming / or / whether / had been counting", "if / dreamed / or / if / counted", "whether / was dreaming / or / whether / would count"],
          a: "whether / had been dreaming / or / whether / had been counting",
          tip: "Whether (formal legal). Whether...or = alternatives. Had been dreaming of + had been counting on = Past Perfect Continuous + collocations.",
          successTip: "WHETHER...OR (alternatives) + HAD BEEN DREAMING OF (Past Perfect Continuous + collocation) + HAD BEEN COUNTING ON (Past Perfect Continuous + collocation). The investigation's core question! 💭🤝⚖️"
        },

        {
          type: "input",
          q: "Closing statement: 'The investigation concluded by asking ___ Elena ___ of creating something lasting — ___ of personal gain — and ___ she had ___ every penny ___ something real.' (whether + dream of / or / whether + pour into — past perfect)",
          a: "whether / had dreamed / or / whether / poured / into",
          alts: ["Whether / had dreamed of / or / whether / had poured / into"],
          successTip: "WHETHER + HAD DREAMED OF...OR + WHETHER + HAD POURED INTO — two collocations, WHETHER...OR structure, Past Perfect throughout. The investigation's final binary question! 💭💧⚖️✅"
        }
      ]
    },

    // ===================================================================
    // SET 9: ALL STRUCTURES COMBINED (B2+)
    // ===================================================================
    {
      id: "m10-u13-set9",
      title: "Set 9: Every Structure Together",
      context: `*Bucharest, the verdict week — all characters*

Priya, Dani, Elena, and Mr. Okoro had all been asked questions.
Some were formal. Some were informal.
Some had two options. Some were personal decisions.
Some were negative. Some were about the future.
Now all of them were in one room — and all the questions
were being reported at the same time.

*Bucarest, la semana del veredicto — todos los personajes*

Priya, Dani, Elena y el Sr. Okoro habían sido interrogados.
Algunas preguntas eran formales. Otras informales.
Algunas tenían dos opciones. Otras eran decisiones personales.
Algunas eran negativas. Otras eran sobre el futuro.
Ahora todos estaban en la misma sala — y todas las preguntas
se estaban reportando al mismo tiempo.`,

      questions: [
        {
          type: "mc",
          q: "Match each sentence to its structure type: 'She asked if they were investing.' / 'She asked whether to invest.' / 'She asked whether to invest or wait.'",
          options: [
            "Yes/No reported question / Whether to + infinitive (decision) / Whether...or (alternatives)",
            "Whether...or / Yes/No / Whether to + infinitive",
            "Whether to / Yes/No / Whether...or"
          ],
          a: "Yes/No reported question / Whether to + infinitive (decision) / Whether...or (alternatives)",
          tip: "If they were investing = yes/no. Whether to invest = decision. Whether to invest or wait = alternatives with decision.",
          successTip: "Three structures identified! IF = yes/no reported question. WHETHER TO = decision/dilemma. WHETHER...OR = alternatives. All three if/whether uses in one question! 🎯"
        },

        {
          type: "mc",
          q: "Priya asked Elena (informal): '___ you ever ___ of giving up?' Elena replied (formal, for the record): 'She asked ___ I ___ of giving up.'",
          options: ["Did / dream / whether / had dreamed", "Have / dreamed / if / dreamed", "Did / dream / if / had dreamed"],
          a: "Did / dream / whether / had dreamed",
          tip: "Direct: Did you dream of? Reported formal (for the record): whether (formal) + had dreamed (backshift). Dream of = collocation.",
          successTip: "DID YOU DREAM OF? → FOR THE RECORD: WHETHER + HAD DREAMED OF. Same question — informal direct → formal reported. Backshift: dreamed → had dreamed. DREAM OF collocation! 💭📋"
        },

        {
          type: "mc",
          q: "Mr. Okoro couldn't decide: '___ to invest more or ___ for the verdict?' Elena asked him: 'He told me he couldn't decide ___ ___ more ___ ___ for the verdict.'",
          options: ["whether / wait / whether to invest / or / wait", "if / wait / if to invest / or / wait", "whether / wait / whether to / invest / or wait"],
          a: "whether / wait / whether to invest / or / wait",
          tip: "Direct: whether to invest or wait for = alternatives with decision. Reported: whether to invest or wait for = same structure. Invest in + wait for = collocations.",
          successTip: "WHETHER TO INVEST IN OR WAIT FOR — the combination of whether...or AND whether to + infinitive in one sentence. Two collocations, one elegant structure! 💰⏳🔄"
        },

        {
          type: "mc",
          q: "Dani was asked (legal): '___ you been counting on Elena's assurances throughout?' → The transcript read: The investigator asked ___ Dani ___ on Elena's assurances throughout.",
          options: ["whether / had been counting", "if / had been counting", "whether / was counting"],
          a: "whether / had been counting",
          tip: "Legal transcript = whether. Had been counting = Present Perfect Continuous → Past Perfect Continuous backshift. Count on = collocation.",
          successTip: "WHETHER (legal transcript) + HAD BEEN COUNTING ON (Present Perfect Continuous → Past Perfect Continuous + collocation). Three decisions, all correct! 🤝⚖️"
        },

        {
          type: "mc",
          q: "The question nobody wanted to answer was ___ Elena ___ accused of something she ___ fought against her whole career — ___ ___ the one ___ against her colleagues.",
          options: ["whether / had been / had / or whether / she / had fought", "if / was / fought / or if / was / fighting", "whether / had been / had / or whether / had been / fighting"],
          a: "whether / had been / had / or whether / she / had fought",
          tip: "Whether (formal). Had been accused of + had fought against = Past Perfect + collocations. Whether...or = alternatives.",
          successTip: "WHETHER + HAD BEEN ACCUSED OF...OR WHETHER + HAD FOUGHT AGAINST — two collocations, WHETHER...OR alternatives, Past Perfect throughout. The most complex question in the case! ⚖️💪"
        },

        {
          type: "mc",
          q: "Everyone wondered ___ Elena ___ ___ into something real — or ___ ___ on a dream that ___ never become reality.",
          options: ["whether / had poured / everything / whether / had counted / would", "if / poured / it / if / counted / will", "whether / was pouring / money / whether / was counting / had"],
          a: "whether / had poured / everything / whether / had counted / would",
          tip: "Whether...or (alternatives). Had poured into + had counted on = Past Perfect + collocations. Would = modal backshift of will.",
          successTip: "WHETHER + HAD POURED INTO...OR WHETHER + HAD COUNTED ON + WOULD — three collocations, WHETHER...OR, Past Perfect + modal backshift. The room's collective question! 💧🤝🔄"
        },

        {
          type: "order",
          q: "Reorder: 'La pregunta central era si Elena había soñado con esto toda su vida o si simplemente había esperado el momento adecuado para actuar.'",
          words: ["The", "central", "question", "was", "whether", "Elena", "had", "dreamed", "of", "this", "her", "whole", "life", "or", "whether", "she", "had", "simply", "waited", "for", "the", "right", "moment", "to", "act."],
          a: "The central question was whether Elena had dreamed of this her whole life or whether she had simply waited for the right moment to act.",
          extra: ["if", "dreamed", "was"]
        },

        {
          type: "mc",
          q: "The lawyer's closing summary used all four if/whether structures. Choose the correct version:",
          options: [
            "The case asked whether she had invested wisely, whether to trust the evidence, whether she had acted legally or not, and whether she hadn't been fighting against injustice all along.",
            "The case asked if she had invested wisely, if to trust the evidence, if she had acted legally or not, and if she hadn't been fighting against injustice all along.",
            "The case asked whether she had invested wisely, if to trust the evidence, whether she had acted legally or not, whether she hadn't been fighting against injustice."
          ],
          a: "The case asked whether she had invested wisely, whether to trust the evidence, whether she had acted legally or not, and whether she hadn't been fighting against injustice all along.",
          tip: "All four structures require WHETHER in formal legal context. Whether (yes/no) + whether to (decision) + whether...or (alternatives) + whether + negative = all whether!",
          successTip: "All four WHETHER structures in legal context: WHETHER (yes/no) + WHETHER TO (decision) + WHETHER...OR (alternatives) + WHETHER + negative. IF appears nowhere in formal legal reporting! ⚖️🏆"
        },

        {
          type: "input",
          q: "Verdict eve: 'Nobody ___ ___ ___ for the verdict or ___ ___ against what was coming — each person simply ___ ___ themselves ___ it ___ be worth it.' (know whether to wait for / fight against / wonder whether to ask / whether — past perfect)",
          a: "knew / whether to wait / for / fight / against / wondered / whether to ask / whether / would",
          alts: ["Knew / whether to wait for / fight against / wondered / whether to ask themselves / whether / would"],
          successTip: "WHETHER TO WAIT FOR + FIGHT AGAINST + WONDERED WHETHER TO ASK + WHETHER + WOULD — four if/whether structures, three collocations, one sentence. Maximum complexity, full control! ⏳💪🤔⚖️✅"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL NARRATIVE (B2+)
    // ===================================================================
    {
      id: "m10-u13-set10",
      title: "Set 10: One Year, Eight Questions",
      context: `*Bucharest, the final day — the verdict*

The verdict was not guilty.
Elena had been found innocent.
Every question that had been asked — formally and informally,
in courtrooms and in conversations — had been answered.
Now Priya was writing the final article.
And the last question was the most important one:
had it all been worth it?

*Bucarest, el día final — el veredicto*

El veredicto fue inocente.
Elena había sido declarada inocente.
Cada pregunta que se había formulado — formal e informalmente,
en salas del tribunal y en conversaciones — había sido respondida.
Ahora Priya estaba escribiendo el artículo final.
Y la última pregunta era la más importante:
¿había valido todo la pena?`,

      questions: [
        {
          type: "mc",
          q: "Year one, month one: Investors asked ___ Elena ___ in renewable energy — and ___ they ___ on her team.",
          options: ["whether / was investing / whether / could count", "if / invested / if / counted", "whether / had invested / whether / would count"],
          a: "whether / was investing / whether / could count",
          tip: "Whether (formal investor context). Was investing = backshift. Could count on = modal backshift + collocation.",
          successTip: "WHETHER × 2 (formal investor meetings) + WAS INVESTING IN (backshift + collocation) + COULD COUNT ON (modal backshift + collocation). The first questions of the year! 💰🤝"
        },

        {
          type: "mc",
          q: "Month three: The board debated ___ to ___ more resources into phase two ___ to ___ for external funding.",
          options: ["whether / pour / or / wait", "if / pour / or / wait", "whether / invest / or / dream"],
          a: "whether / pour / or / wait",
          tip: "Whether to...or = decision + alternatives. Pour into + wait for = collocations.",
          successTip: "WHETHER TO POUR INTO OR WAIT FOR — decision + alternatives + two collocations! This is the richest if/whether structure! 💧⏳🔄"
        },

        {
          type: "mc",
          q: "Month six: Dani wondered ___ ___ on Elena's promise or ___ for something safer.",
          options: ["whether to count / wait", "whether to count / to wait", "if to count / wait"],
          a: "whether to count / wait",
          tip: "Whether to count on or wait for = whether to + infinitive with alternatives. Count on + wait for = collocations. Parallel infinitives (no repeated to).",
          successTip: "WHETHER TO COUNT ON OR WAIT FOR — two collocations, one decision, parallel infinitives (no repeated TO). Dani's six-month dilemma in one structure! 🤝⏳"
        },

        {
          type: "mc",
          q: "Month nine: The investigation asked ___ Elena ___ of misusing funds — and ___ she ___ against oversight.",
          options: ["whether / had been accused / whether / had been fighting", "if / was accused / if / fought", "whether / accused / whether / fought"],
          a: "whether / had been accused / whether / had been fighting",
          tip: "Legal context = whether. Had been accused of + had been fighting against = Past Perfect Continuous + collocations.",
          successTip: "WHETHER × 2 (legal) + HAD BEEN ACCUSED OF + HAD BEEN FIGHTING AGAINST — both Past Perfect Continuous, both collocations, both legal questions. Month nine! ⚖️💪"
        },

        {
          type: "mc",
          q: "Month eleven: Nobody knew ___ Elena ___ been ___ in something real — ___ ___ of an impossible dream.",
          options: ["whether / had / pouring / or / dreaming", "if / was / investing / or / waiting", "whether / had / investing / or / dreaming"],
          a: "whether / had / pouring / or / dreaming",
          tip: "Whether...or = alternatives. Had been pouring into = Past Perfect Continuous. Had been dreaming of = Past Perfect Continuous. Two collocations.",
          successTip: "WHETHER + HAD BEEN POURING INTO...OR DREAMING OF — two Past Perfect Continuous collocations in WHETHER...OR. The public's question during month eleven! 💧💭🔄"
        },

        {
          type: "mc",
          q: "The verdict day: The judge's final question was ___ the company ___ for the communities it ___ to ___ for — ___ it ___ only ___ in profit.",
          options: ["whether / had provided / had promised / provide / or / had invested / in", "if / provided / promised / providing / or / invested / in", "whether / was providing / promised / provide / or / was investing / in"],
          a: "whether / had provided / had promised / provide / or / had invested / in",
          tip: "Whether...or (legal alternatives). Had provided for + had promised to provide for + had invested in = Past Perfect + collocations.",
          successTip: "WHETHER + HAD PROVIDED FOR...OR + HAD INVESTED IN — three collocations (provide for × 2 + invest in), WHETHER...OR, Past Perfect throughout. The judge's final question! 🏠💰⚖️"
        },

        {
          type: "mc",
          q: "After the verdict, Dani told Elena: 'I always ___ ___ on you — I just ___ ___ admit it.' Elena replied that he ___ told her he ___ always ___ on her but ___ ___ to admit it.",
          options: ["counted / whether to / couldn't / had / had / counted / hadn't / known / whether", "knew / whether to / didn't / said / had / counted on / hadn't / known / whether", "counted / whether to / couldn't / had / had always / counted on / hadn't known / whether"],
          a: "counted / whether to / couldn't / had / had always / counted on / hadn't known / whether",
          tip: "Count on (collocation). Whether to admit (decision). Couldn't = inability. Reported: had told, had always counted on, hadn't known whether.",
          successTip: "COUNTED ON + WHETHER TO ADMIT + HAD ALWAYS COUNTED ON + HADN'T KNOWN WHETHER — the count on collocation appears three times! Direct speech reported twice! 🤝🔄"
        },

        {
          type: "order",
          q: "Reorder: 'Priya preguntó si Elena había soñado con este momento, si había esperado a que llegara, y si habría luchado contra las acusaciones si hubiera sabido el coste.'",
          words: ["Priya", "asked", "whether", "Elena", "had", "dreamed", "of", "this", "moment,", "whether", "she", "had", "waited", "for", "it,", "and", "whether", "she", "would", "have", "fought", "against", "the", "charges", "if", "she", "had", "known", "the", "cost."],
          a: "Priya asked whether Elena had dreamed of this moment, whether she had waited for it, and whether she would have fought against the charges if she had known the cost.",
          extra: ["if", "dreamed", "was"]
        },

        {
          type: "mc",
          q: "Priya's final article used all eight collocations. Choose the correct version:",
          options: [
            "She had invested in a dream, counted on her team, provided for her community, dreamed of something lasting, poured everything into the project, fought against every accusation, waited for justice — and in the end, was never accused of giving up.",
            "She had invested to a dream, counted from her team, provided about her community, dreamed about something lasting, poured everything to the project, fought with every accusation, waited to justice — and was never accused for giving up.",
            "She invested in a dream, counted on her team, provided to her community, dreamed for something lasting, poured everything into the project, fought against every accusation, waited for justice — and was never accused from giving up."
          ],
          a: "She had invested in a dream, counted on her team, provided for her community, dreamed of something lasting, poured everything into the project, fought against every accusation, waited for justice — and in the end, was never accused of giving up.",
          tip: "All eight collocations with their correct prepositions: in, on, for, of, into, against, for, of.",
          successTip: "ALL EIGHT COLLOCATIONS with correct prepositions: invested IN, counted ON, provided FOR, dreamed OF, poured INTO, fought AGAINST, waited FOR, accused OF. Every preposition fixed! 🏆"
        },

        {
          type: "input",
          q: "Final line: 'The only question left was ___ it ___ all ___ the wait — and Priya ___ ___ herself ___ even ___ to ask it.' (whether + be worth / whether to + know + dare)",
          a: "whether / had / been / worth / wondered / whether / to / dare",
          alts: ["Whether / had been / worth / wondered / whether to dare"],
          successTip: "🏆 UNIT 13 COMPLETE! If vs whether ✅ Whether...or ✅ Whether to + infinitive ✅ After prepositions = whether ✅ Register (formal/informal) ✅ Full backshift ✅ Negative backshift ✅ All 8 verb + preposition collocations ✅ You report questions — in every form — with precision! ⚖️💰🌍"
        }
      ]
    }
  ],

  // ===================================================================
  //
  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u13-reading",
    title: "Reading: The Questions That Decided Everything",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**The First Meeting**

Mr. Okoro asked whether Elena was investing in solar or wind technology. She said she was investing in both. He also asked whether the team could be counted on to deliver results within eighteen months. Elena said they could.`
      },
      {
        type: "mc",
        q: "What did Mr. Okoro ask about the technology?",
        options: ["Whether Elena was investing in solar or wind", "Whether she had poured money into infrastructure", "Whether she had dreamed of expanding internationally"],
        a: "Whether Elena was investing in solar or wind",
        tip: "'Mr. Okoro asked whether Elena was investing in solar or wind technology.'"
      },
      {
        type: "mc",
        q: "What did he ask about the team?",
        options: ["Whether they could be counted on to deliver results", "Whether they had fought against the regulation", "Whether they had waited for official approval"],
        a: "Whether they could be counted on to deliver results",
        tip: "'He also asked whether the team could be counted on to deliver results.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**Dani's Dilemma**

Dani couldn't decide whether to count on Elena's promise or wait for a better offer. He asked himself whether he had always dreamed of working on a project like this. He had. That was enough.`
      },
      {
        type: "mc",
        q: "What couldn't Dani decide?",
        options: ["Whether to count on Elena's promise or wait for a better offer", "Whether to invest in the project or fight against it", "Whether to provide for his family or pour money into the startup"],
        a: "Whether to count on Elena's promise or wait for a better offer",
        tip: "'Dani couldn't decide whether to count on Elena's promise or wait for a better offer.'"
      },
      {
        type: "mc",
        q: "What did Dani ask himself?",
        options: ["Whether he had always dreamed of working on a project like this", "Whether he had been accused of anything", "Whether he had waited too long to decide"],
        a: "Whether he had always dreamed of working on a project like this",
        tip: "'He asked himself whether he had always dreamed of working on a project like this.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**The Investigation**

The investigators asked whether Elena had been accused of misusing funds before. They also asked whether she had been fighting against the regulation or working within it. Elena's lawyer noted that the question of whether she had waited for official approval was central to the case.`
      },
      {
        type: "mc",
        q: "What did the investigators ask about Elena's past?",
        options: ["Whether she had been accused of misusing funds before", "Whether she had poured money into private accounts", "Whether she had counted on government support"],
        a: "Whether she had been accused of misusing funds before",
        tip: "'The investigators asked whether Elena had been accused of misusing funds before.'"
      },
      {
        type: "mc",
        q: "What question was central to the case?",
        options: ["Whether she had waited for official approval", "Whether she had dreamed of personal gain", "Whether she had fought against the charges"],
        a: "Whether she had waited for official approval",
        tip: "'...the question of whether she had waited for official approval was central to the case.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Priya's Profile**

Priya asked Elena whether she had always dreamed of building something lasting or whether she had simply waited for the right moment. Elena replied that she had dreamed of it since university — and had never stopped fighting against the idea that it was impossible.`
      },
      {
        type: "mc",
        q: "What did Priya ask Elena?",
        options: ["Whether she had always dreamed of building something lasting or waited for the right moment", "Whether she had invested in solar or wind technology", "Whether she had poured her savings into the first phase"],
        a: "Whether she had always dreamed of building something lasting or waited for the right moment",
        tip: "'Priya asked Elena whether she had always dreamed of building something lasting or whether she had simply waited for the right moment.'"
      },
      {
        type: "mc",
        q: "What had Elena never stopped doing?",
        options: ["Fighting against the idea that it was impossible", "Waiting for official approval", "Counting on government support"],
        a: "Fighting against the idea that it was impossible",
        tip: "'...had never stopped fighting against the idea that it was impossible.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Verdict**

After the verdict, Priya wrote her final article. She noted that nobody had ever doubted whether Elena had poured everything into the project. The only question had been whether she had done it legally. She had. The question of whether it had all been worth it answered itself.`
      },
      {
        type: "mc",
        q: "What had nobody ever doubted?",
        options: ["Whether Elena had poured everything into the project", "Whether she had counted on the right people", "Whether she had invested in the right technology"],
        a: "Whether Elena had poured everything into the project",
        tip: "'Nobody had ever doubted whether Elena had poured everything into the project.'"
      },
      {
        type: "mc",
        q: "How did the final question answer itself?",
        options: ["The verdict confirmed it had been worth it", "Elena admitted it had not been worth it", "The investors decided to provide for the next phase"],
        a: "The verdict confirmed it had been worth it",
        tip: "'The question of whether it had all been worth it answered itself.' — the not guilty verdict was the answer.",
        successTip: "All eight collocations appeared across the five passages! Invest in ✅ Count on ✅ Provide for ✅ Dream of ✅ Pour into ✅ Accused of ✅ Fight against ✅ Wait for ✅ 🏆"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u13-review",
    title: "Unit 13 Review: If / Whether + Verb + Preposition",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: He asked ___ the company ___ in renewable energy.",
        options: ["whether / was investing", "if / is investing", "whether / invested"],
        a: "whether / was investing",
        tip: "Whether = formal. Is investing → was investing (backshift). Invest in = collocation."
      },
      {
        type: "mc",
        q: "Review: She couldn't decide ___ ___ on the government's support.",
        options: ["whether to count", "if to count", "whether counting"],
        a: "whether to count",
        tip: "Decision/dilemma = whether to + infinitive. NEVER if to. Count on = collocation."
      },
      {
        type: "mc",
        q: "Review: The board asked ___ she ___ in domestic ___ international markets.",
        options: ["whether / would invest / or", "if / would invest / or", "whether / invested / or"],
        a: "whether / would invest / or",
        tip: "Alternatives = whether...or (NEVER if...or). Would invest = modal backshift. Invest in = collocation."
      },
      {
        type: "mc",
        q: "Review: The document raised the question of ___ the funds ___ misused.",
        options: ["whether / had been", "if / had been", "whether / were"],
        a: "whether / had been",
        tip: "After preposition 'of' = whether only (never if). Had been = Past Perfect backshift."
      },
      {
        type: "mc",
        q: "Review: The investigator asked ___ she ___ of mismanagement before.",
        options: ["whether / had been accused", "if / was accused", "whether / accused"],
        a: "whether / had been accused",
        tip: "Legal formal = whether. Had been accused = Past Perfect passive. Accused of = collocation."
      },
      {
        type: "mc",
        q: "Review: Dani asked Elena (casual): '___ you still dreaming of expanding?'",
        options: ["Are / if is fine in casual speech", "Whether / only whether is correct", "Are / whether is required here"],
        a: "Are / if is fine in casual speech",
        tip: "Casual speech between colleagues = if is perfectly correct. Register determines if vs whether."
      },
      {
        type: "mc",
        q: "Review: She asked ___ the project ___ for local communities or only for investors.",
        options: ["whether / would provide / or", "if / provides / or", "whether / provided / or"],
        a: "whether / would provide / or",
        tip: "Whether...or = alternatives. Would provide = modal backshift. Provide for = collocation."
      },
      {
        type: "mc",
        q: "Review: The question was ___ she ___ ___ the charges or ___ for a settlement.",
        options: ["whether / would fight against / wait", "if / fights against / waits", "whether / fought against / waited"],
        a: "whether / would fight against / wait",
        tip: "Whether...or (alternatives). Would fight against + wait for = modal backshift + collocations.",
      },
      {
        type: "mc",
        q: "Review: Nobody knew ___ Elena ___ everything ___ something real or ___ of personal gain.",
        options: ["whether / had poured / into / dreamed", "if / poured / into / dreamed", "whether / was pouring / into / dreaming"],
        a: "whether / had poured / into / dreamed",
        tip: "Whether...or (alternatives). Had poured into + dreamed of = Past Perfect + collocations."
      },
      {
        type: "mc",
        q: "Review: After the verdict, Elena told Dani she had never stopped ___ ___ the project — and had always known he ___ ___ ___.",
        options: ["believing in / could be counted on", "dreaming about / would count on", "fighting for / could be counted on"],
        a: "believing in / could be counted on",
        tip: "Believing in the project + could be counted on (passive) = backshifted modal + collocation.",
        successTip: "🎉 UNIT 13 COMPLETE! If vs whether ✅ Whether...or ✅ Whether to + infinitive ✅ After prepositions = whether ✅ Register formal/informal ✅ Full backshift ✅ All 8 collocations ✅ invest in · count on · provide for · dream of · pour into · accused of · fight against · wait for ✅ 🏆🌍"
      }
    ]
  }
};
